// F113 (Pavel 2026-08-10): ONE vocabulary for why a bot transfer did not
// happen. The worker validates the machine report against it, the client's beta
// step decides from it whether the client can fix the problem, and the Servis
// queue renders it. A second copy is how the client-facing allowlist and the
// server's list drifted apart in the first place.
//
// The Mac Mini runner mirrors this list in buddypro-instance/launchpad_bot_transfer.py
// (KNOWN_REASONS); a reason missing there is reported as "unknown", never dropped.
//
// 🔴 A transfer counts as DONE only when a fresh /mybots read proved the bot
// left our pool account. There is no reason code for "BotFather sounded happy":
// text names the cause, the bot's absence is the proof.

/**
 * The client can act on these: the step names the reason and offers the
 * ownership form again (F44). Every one of them is about the ACCOUNT the client
 * typed in, which is the only part of the transfer they control.
 */
export const BOT_TRANSFER_CLIENT_REASONS = [
  // The recipient never wrote to the bot; Telegram refuses the transfer then.
  "recipient_not_started",
  // No such Telegram username.
  "recipient_not_found",
  // Verified live 2026-08-10 16:43: "Oops! <Name> has reached the maximum
  // allowed number of bots." The recipient must free a slot on their own
  // account first - nothing on our side can help.
  "recipient_bot_limit",
  // Verified live 2026-08-10 16:16: "Bot ownership can't be transferred to
  // channel, another bot or yourself." The client filled in a bot or a channel
  // (the live case was the bot's own username).
  "recipient_invalid_target"
] as const;

/**
 * Ours to solve. The client keeps the honest "pracujeme" and the partner gets
 * the F94 notification with the client's name in the title.
 */
export const BOT_TRANSFER_INTERNAL_REASONS = [
  "floodwait",
  "twofa_too_fresh",
  "gate_mismatch",
  "bot_not_found",
  "session_unavailable",
  // F113: the confirmation went out but /mybots could not be read, so nobody
  // knows whether the bot changed hands. NEVER done - a human looks first.
  "verification_unavailable",
  "unknown"
] as const;

export const BOT_TRANSFER_REASONS = [
  ...BOT_TRANSFER_CLIENT_REASONS,
  ...BOT_TRANSFER_INTERNAL_REASONS
] as const;

export type BotTransferClientReason = (typeof BOT_TRANSFER_CLIENT_REASONS)[number];
export type BotTransferInternalReason = (typeof BOT_TRANSFER_INTERNAL_REASONS)[number];
export type BotTransferReason = BotTransferClientReason | BotTransferInternalReason;

export function isBotTransferClientFault(reason: string): reason is BotTransferClientReason {
  return (BOT_TRANSFER_CLIENT_REASONS as readonly string[]).includes(reason);
}

export function isBotTransferReason(reason: string): reason is BotTransferReason {
  return (BOT_TRANSFER_REASONS as readonly string[]).includes(reason);
}
